<template>
  <div
    class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-12 px-4 sm:px-6 lg:px-8"
  >
    <!-- Animated background elements -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden">
      <div
        class="absolute top-20 left-10 w-72 h-72 bg-blue-400/5 rounded-full blur-3xl animate-pulse"
      ></div>
      <div
        class="absolute bottom-20 right-10 w-72 h-72 bg-cyan-400/5 rounded-full blur-3xl animate-pulse"
        style="animation-delay: 2s"
      ></div>
    </div>

    <div class="relative z-10 max-w-5xl mx-auto">
      <!-- Header Section -->
      <div class="mb-12">
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-1 h-8 bg-gradient-to-b from-blue-600 to-cyan-600 rounded"
          ></div>
          <h1 class="text-5xl font-black text-slate-900 tracking-tight">
            Announcements
          </h1>
        </div>
        <p class="text-slate-600 text-lg ml-4">
          Stay informed with the latest updates and important notifications
        </p>
      </div>

      <!-- Admin Create Button -->
      <div
        v-if="isAdmin"
        class="mb-10"
      >
        <button
          @click="toggleCreateForm"
          class="group relative inline-flex items-center justify-center gap-2 px-8 py-4 font-semibold text-white rounded-xl transition-all duration-300"
          :class="[
            showCreateForm
              ? 'bg-red-500/15 border border-red-400 text-red-700 hover:bg-red-500/25'
              : 'bg-gradient-to-r from-blue-600 to-cyan-600 border border-blue-300/50 hover:from-blue-700 hover:to-cyan-700 hover:shadow-lg hover:shadow-blue-400/40',
          ]"
        >
          <span
            class="text-xl"
            :class="showCreateForm ? '✕' : '✚'"
          ></span>
          {{ showCreateForm ? "Cancel" : "New Announcement" }}
        </button>
      </div>

      <!-- Create/Edit Form -->
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-95 translate-y-4"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 scale-100 translate-y-0"
        leave-to-class="opacity-0 scale-95 -translate-y-4"
      >
        <div
          v-if="showCreateForm && isAdmin"
          class="mb-10 backdrop-blur-sm bg-white/80 rounded-2xl border border-blue-200 p-8 shadow-lg"
        >
          <h2
            class="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-2"
          >
            <span
              class="w-1 h-6 bg-gradient-to-b from-blue-600 to-cyan-600 rounded"
            ></span>
            {{ editingId ? "Edit Announcement" : "Create New Announcement" }}
          </h2>

          <form
            @submit.prevent="submitForm"
            class="space-y-6"
          >
            <!-- Title -->
            <div class="group">
              <label class="block text-sm font-semibold text-slate-700 mb-3"
                >Title</label
              >
              <input
                v-model="formData.title"
                type="text"
                placeholder="Enter an engaging title..."
                class="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all duration-200"
                required
              />
            </div>

            <!-- Message -->
            <div class="group">
              <label class="block text-sm font-semibold text-slate-700 mb-3"
                >Message</label
              >
              <textarea
                v-model="formData.message"
                placeholder="Write your announcement message..."
                rows="5"
                class="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all duration-200 resize-none"
                required
              ></textarea>
            </div>

            <!-- Target Role & Expiry Row -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <!-- Target Role -->
              <div class="group">
                <label class="block text-sm font-semibold text-slate-700 mb-3"
                  >Target Role</label
                >
                <select
                  v-model="formData.targetRole"
                  class="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all duration-200"
                >
                  <option value="">All Users</option>
                  <option value="admin">Admin</option>
                  <option value="teacher">Teacher</option>
                  <option value="student">Student</option>
                  <option value="parent">Parent</option>
                </select>
              </div>

              <!-- Expiry Date -->
              <div class="group">
                <label class="block text-sm font-semibold text-slate-700 mb-3"
                  >Expiry Date (Optional)</label
                >
                <input
                  v-model="formData.expiryDate"
                  type="datetime-local"
                  class="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all duration-200"
                />
              </div>
            </div>

            <!-- Pinned Checkbox -->
            <div class="flex items-center gap-3">
              <input
                v-model="formData.isPinned"
                type="checkbox"
                id="pinned"
                class="w-5 h-5 rounded-lg bg-white border border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-2 cursor-pointer"
              />
              <label
                for="pinned"
                class="text-sm font-semibold text-slate-700 cursor-pointer"
              >
                📌 Pin this announcement to the top
              </label>
            </div>

            <!-- Submit Buttons -->
            <div class="flex gap-3 pt-6">
              <button
                type="submit"
                :disabled="isSubmitting"
                class="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg hover:shadow-green-500/40"
              >
                {{
                  isSubmitting ? "Saving..." : editingId ? "Update" : "Create"
                }}
              </button>
              <button
                type="button"
                @click="resetForm"
                class="flex-1 px-6 py-3 bg-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-300 border border-slate-300 transition-all duration-200"
              >
                Clear
              </button>
            </div>
          </form>
        </div>
      </transition>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="space-y-4"
      >
        <div
          v-for="i in 3"
          :key="i"
          class="backdrop-blur-sm bg-slate-200/40 rounded-2xl border border-slate-300 p-8 animate-pulse"
        >
          <div class="flex items-start gap-4">
            <div class="flex-1 space-y-3">
              <div class="h-6 bg-slate-300/60 rounded-lg w-1/3"></div>
              <div class="h-4 bg-slate-300/60 rounded-lg w-full"></div>
              <div class="h-4 bg-slate-300/60 rounded-lg w-5/6"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Announcements List -->
      <div
        v-else-if="filteredAnnouncements && filteredAnnouncements.length > 0"
        class="space-y-5"
      >
        <transition-group
          name="list"
          tag="div"
          class="space-y-5"
        >
          <div
            v-for="announcement in filteredAnnouncements"
            :key="announcement._id"
            class="group relative backdrop-blur-sm rounded-2xl border transition-all duration-300 hover:shadow-lg overflow-hidden"
            :class="[
              announcement.isPinned
                ? 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-300 shadow-md'
                : 'bg-white border-slate-300 hover:border-blue-300 hover:shadow-md',
            ]"
          >
            <!-- Pinned Badge Background -->
            <div
              v-if="announcement.isPinned"
              class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-400/5 to-transparent rounded-full blur-2xl"
            ></div>

            <div class="relative p-8 sm:p-10">
              <!-- Header -->
              <div class="flex items-start justify-between mb-5 gap-4">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-3 flex-wrap mb-2">
                    <h3 class="text-2xl font-bold text-slate-900 truncate">
                      {{ announcement.title }}
                    </h3>
                    <transition name="badge">
                      <span
                        v-if="announcement.isPinned"
                        class="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 text-xs font-bold rounded-full border border-blue-300 whitespace-nowrap"
                      >
                        📌 Pinned
                      </span>
                    </transition>
                  </div>
                  <p class="text-sm text-slate-600">
                    By
                    <span class="font-semibold text-slate-700">{{
                      announcement.createdBy?.name || "Admin"
                    }}</span>
                    <span class="text-slate-500"
                      >• {{ formatDate(announcement.createdAt) }}</span
                    >
                  </p>
                </div>
              </div>

              <!-- Message -->
              <p
                class="text-slate-700 text-base leading-relaxed mb-6 whitespace-pre-wrap"
              >
                {{ announcement.message }}
              </p>

              <!-- Meta Info -->
              <div class="flex flex-wrap gap-3 mb-6">
                <span
                  v-if="announcement.targetRole"
                  class="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg border border-blue-300"
                >
                  👥
                  {{
                    announcement.targetRole.charAt(0).toUpperCase() +
                    announcement.targetRole.slice(1)
                  }}
                </span>
                <span
                  v-if="announcement.expiryDate"
                  class="inline-flex items-center px-4 py-2 bg-amber-100 text-amber-700 text-xs font-semibold rounded-lg border border-amber-300"
                >
                  ⏰ Expires {{ formatDate(announcement.expiryDate) }}
                </span>
                <span
                  class="inline-flex items-center px-4 py-2 bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg border border-slate-300"
                >
                  👁️ {{ announcement.readBy?.length || 0 }} read
                </span>
              </div>

              <!-- Actions -->
              <div class="flex gap-3 flex-wrap">
                <button
                  v-if="!isAnnouncementRead(announcement._id)"
                  @click="markAsRead(announcement._id)"
                  class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white text-sm font-semibold rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all duration-200 hover:shadow-lg hover:shadow-green-500/40"
                >
                  ✓ Mark as Read
                </button>
                <span
                  v-else
                  class="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-lg border border-emerald-300"
                >
                  ✓ Read
                </span>

                <button
                  v-if="isAdmin"
                  @click="editAnnouncement(announcement)"
                  class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all duration-200 hover:shadow-lg hover:shadow-amber-500/40"
                >
                  ✎ Edit
                </button>

                <button
                  v-if="isAdmin"
                  @click="removeAnnouncement(announcement._id)"
                  class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 text-white text-sm font-semibold rounded-lg hover:from-red-700 hover:to-rose-700 transition-all duration-200 hover:shadow-lg hover:shadow-red-500/40"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="backdrop-blur-sm bg-slate-100 rounded-2xl border border-slate-300 p-16 text-center"
      >
        <div class="mb-4 text-5xl">📢</div>
        <p class="text-slate-700 text-xl font-semibold mb-2">
          No Announcements Yet
        </p>
        <p class="text-slate-600">
          Announcements will appear here when they're created
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from "vue";
  import { useStore } from "vuex";

  const store = useStore();

  /* ---------------- STATE (UNCHANGED LOGIC) ---------------- */
  const showCreateForm = ref(false);
  const isSubmitting = ref(false);
  const editingId = ref(null);

  const formData = ref({
    title: "",
    message: "",
    targetRole: "",
    expiryDate: "",
    isPinned: false,
  });

  /* ---------------- STORE STATE (UNCHANGED LOGIC) ---------------- */
  const announcements = computed(() => store.state.announcements);
  const loading = computed(() => store.state.announcementsLoading);
  const userRole = computed(() => store.state.userRole);
  const userId = computed(() => store.state.userId);

  const isAdmin = computed(() => userRole.value === "admin");

  /* ---------------- FILTER (UNCHANGED LOGIC) ---------------- */
  const filteredAnnouncements = computed(() => {
    if (!Array.isArray(announcements.value)) return [];

    return announcements.value.slice().sort((a, b) => {
      if (a?.isPinned && !b?.isPinned) return -1;
      if (!a?.isPinned && b?.isPinned) return 1;
      return new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
    });
  });

  /* ---------------- METHODS (UNCHANGED LOGIC) ---------------- */

  function toggleCreateForm() {
    showCreateForm.value = !showCreateForm.value;
    if (!showCreateForm.value) resetForm();
  }

  function resetForm() {
    formData.value = {
      title: "",
      message: "",
      targetRole: "",
      expiryDate: "",
      isPinned: false,
    };
    editingId.value = null;
  }

  async function submitForm() {
    isSubmitting.value = true;

    try {
      const payload = {
        ...formData.value,
        expiryDate: formData.value.expiryDate
          ? new Date(formData.value.expiryDate).toISOString()
          : null,
      };

      if (editingId.value) {
        await store.dispatch("updateAnnouncement", {
          id: editingId.value,
          data: payload,
        });
      } else {
        await store.dispatch("createAnnouncement", payload);
      }

      resetForm();
      showCreateForm.value = false;
    } catch (err) {
      console.error(err);
      alert("Failed to save announcement");
    } finally {
      isSubmitting.value = false;
    }
  }

  function editAnnouncement(announcement) {
    formData.value = {
      title: announcement.title,
      message: announcement.message,
      targetRole: announcement.targetRole || "",
      expiryDate: announcement.expiryDate
        ? announcement.expiryDate.slice(0, 16)
        : "",
      isPinned: announcement.isPinned,
    };

    editingId.value = announcement._id;
    showCreateForm.value = true;
  }

  async function removeAnnouncement(id) {
    if (!confirm("Delete this announcement?")) return;
    await store.dispatch("deleteAnnouncement", id);
  }

  async function markAsRead(id) {
    await store.dispatch("markAnnouncementRead", id);
  }

  function isAnnouncementRead(id) {
    if (!Array.isArray(announcements.value) || !userId.value) return false;

    const ann = announcements.value.find((a) => a?._id === id);
    if (!ann || !Array.isArray(ann.readBy)) return false;

    return ann.readBy.some((r) => r?.userId === userId.value);
  }

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /* ---------------- MOUNT (UNCHANGED LOGIC) ---------------- */
  onMounted(() => {
    store.dispatch("fetchAnnouncements");
  });
</script>

<style scoped>
  /* Transition animations */
  .list-enter-active,
  .list-leave-active {
    transition: all 0.3s ease;
  }

  .list-enter-from {
    opacity: 0;
    transform: translateX(-30px);
  }

  .list-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }

  .badge-enter-active,
  .badge-leave-active {
    transition: all 0.3s ease;
  }

  .badge-enter-from,
  .badge-leave-to {
    opacity: 0;
    transform: scale(0.8);
  }
</style>
